<script lang="ts" setup>
import countTo from 'vue-count-to'
import { onUnmounted, ref } from 'vue'
import type { MemberTopupSubscribeProduct, SubscribePageConfig } from '@/composables/payment'
import {
  PaymentMethodSelector,
  createShowPaymentMethodSelector,
  useSubscription,
} from '@/composables/payment'
import { VIP_TYPE } from '@/constants'
import { brand, colors } from '@/config/brands'

const props = defineProps<{
  config: SubscribePageConfig
}>()

// 使用订阅 composable（支持多支付方式）
const {
  subscribeProducts,
  getUnitPrice,
  onSubscribeProducts,
  getIsImageBackground,
  isZero,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
} = useSubscription('store')

// 是否显示支付方式选择器
const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

const active = ref(0)
const discountModel = ref(false)

const timer = ref<ReturnType<typeof setTimeout> | null>(null)
const currentDiscount: any = ref(null)
const discountNumber = ref(0)
const getDiscountPercentage = function () {
  if (subscribeProducts.value && subscribeProducts.value.length > 0) {
    let maxDiscount = 0
    let current = null
    subscribeProducts.value.forEach((item: MemberTopupSubscribeProduct) => {
      let discount = 0
      let shouldCalculate = false

      // 畅读会员：只有在 preferentialPrice 存在时才计算折扣(包括为0的情况)
      if (item.subscribeType === VIP_TYPE.ALL && (item.preferentialPrice !== undefined && item.preferentialPrice !== null)) {
        discount = (1 - (Number(item.preferentialPrice) / Number(item.price))) * 100
        shouldCalculate = true
      }

      // 折扣会员：有 discountOriginalPrice 就计算折扣
      if (item.subscribeType === VIP_TYPE.DISCOUNT && item.discountOriginalPrice) {
        discount = (1 - (Number(item.price) / Number(item.discountOriginalPrice))) * 100
        shouldCalculate = true
      }

      // 只有符合条件的会员才参与比较
      if (shouldCalculate && discount >= maxDiscount) {
        current = item
        maxDiscount = Math.round(discount)
      }
    })

    // 只要找到符合条件的会员就返回，不管折扣是多少(包括0)
    if (current)
      return [current, maxDiscount]
  }

  return false
}

function initDiscountModel() {
  const showDiscount = window.localStorage.getItem('showDiscount')
  if (showDiscount)
    return

  timer.value = setTimeout(() => {
    const discountResult = getDiscountPercentage()
    discountNumber.value = discountResult ? (discountResult[1] ?? 0) : 0
    currentDiscount.value = discountResult ? discountResult[0] : null

    if (!Array.isArray(discountResult)) {
      clearTimeout(timer.value!)
      return
    }

    discountModel.value = true
    window.localStorage.setItem('showDiscount', '1')
  }, 6000)
}
initDiscountModel()

function buyNow() {
  onSubscribeProducts(currentDiscount.value)
}

onUnmounted(() => {
  if (timer.value)
    clearTimeout(timer.value)
})

// 定义样式变量供 CSS v-bind 使用
const redGradient = props.config.colors.redGradient
const whiteColor = '#FFFFFF'
const purpleTextGradient = props.config.colors.purpleTextGradient
const vipBg = `${props.config.resources.vipBgColor} url(${props.config.resources.vipBg}) no-repeat top left`
const primaryColor = colors?.primary

const subscribeText1 = props.config.colors.subscribeText1
const subscribeText2 = props.config.colors.subscribeText2
const subscribeText3 = props.config.colors.subscribeText3
const subscribeText4 = props.config.colors.subscribeText4
</script>

<template>
  <div class="subscribe-cover h-100dvh">
    <BasicNavbar class="text-white" />

    <div class="px-24px">
      <div class="subscribe-title text-20px font-bold h-44px flex items-center justify-center">
        {{ config.texts.vipMembership }}
      </div>
      <div class="subscribe-text-2 text-14px">
        {{ config.texts.membershipDesc }}
      </div>
      <div class="subscribe-text-3 mt-2px mb-16px text-14px">
        {{ config.texts.notJoinedYet }}
      </div>
    </div>

    <div class="member-wrap px-16px bg-#100f17">
      <div v-for="(item, index) in subscribeProducts" :key="index">
        <div
          v-if="item.isShow"
          class="px-14px pt-20px pb-16px border-rd-12px subscribe-text-2 relative mb-10px"
          :style="{
            border: active === index ? `2px solid ${primaryColor}` : `2px solid #2C273D`,
            background: config.resources.memberSubscribeBg
              ? `url(${config.resources.memberSubscribeBg}) no-repeat`
              : config.resources.memberSubscribeBgColor,
            backgroundSize: '100% 100%',
            color: getIsImageBackground(item) ? subscribeText4 : subscribeText2,
          }"
          @click="() => { active = index; onSubscribeProducts(item); }"
        >
          <!-- 角标, 两种会员都有 -->
          <div v-if="item.cornerTip && item.marketingCopy" class="popular-tag">
            {{ item.marketingCopy }}
          </div>

          <!-- 折扣会员 -->
          <div v-if="item.subscribeType === VIP_TYPE.DISCOUNT" class="flex items-center">
            <div class="absolute top-0 right-0 font-bold text-28px leading-[28px] cycleBg">
              {{ item.cycleStr }}
            </div>

            <img v-if="brand !== 'ficmoon'" class="flex-shrink-0 mr-16px" src="/images/vowpath/coins/vip-badge.webp" width="22">
            <div class="flex-1">
              <div class="text-18px font-medium">
                {{ item.discountCoins }} Diamonds
              </div>
              <ul
                class="text-11px list-disc pl-3"
                :class="getIsImageBackground(item) ? 'subscribe-text-4' : 'subscribe-text-1'"
              >
                <li>{{ config.texts.payTemplateGetCoins?.(item.discountOnceCoins) }}</li>
                <li>{{ config.texts.payTemplateEarnCoins?.(item.discountBonusCoins, item.discountDailyCoins) }}</li>
              </ul>
            </div>
            <div class="flex-shrink-0 text-right flex flex-col items-end gap-4px">
              <div class="discount-coins-text">
                $ <countTo
                  :start-val="Number(item.discountOriginalPrice)"
                  :end-val="Number(item.preferentialPrice || item.price)"
                  :duration="3000"
                  :decimals="2"
                />
              </div>
              <div class="text-12px line-through" :class="getIsImageBackground(item) ? 'subscribe-text-4' : 'subscribe-text-3'">
                $ {{ item.discountOriginalPrice }}
              </div>
            </div>
          </div>

          <!-- 畅读会员 -->
          <div v-else class="flex items-center">
            <img v-if="brand !== 'ficmoon'" class="flex-shrink-0 mr-16px" :src="config.resources.vipIcon" width="22">
            <div class="flex-1">
              <div class="text-18px font-medium">
                {{ item.cycleStr }}
              </div>
              <div class="subscribe-text-1">
                {{ item.subscribeText }}
              </div>
            </div>

            <div class="flex-shrink-0 text-right flex flex-col items-end gap-4px">
              <!-- 免费 -->
              <template v-if="isZero(item.preferentialPrice)">
                <div class="price-btn-subscribe text-13px font-medium text-white">
                  {{ config.texts.free }}
                </div>
                <div class="subscribe-text-3 text-12px line-through">
                  $ {{ item.price }}
                </div>
              </template>
              <!-- 无优惠价格 -->
              <template v-else-if="!item.preferentialPrice">
                <div class="price-btn-subscribe text-13px font-medium text-white">
                  $ {{ item.price }}
                </div>
                <div v-if="getUnitPrice(item)" class="subscribe-text-3 text-12px">
                  $ {{ getUnitPrice(item) }}
                </div>
              </template>
              <!-- 有优惠价格 -->
              <template v-else>
                <div class="price-btn-subscribe text-13px font-medium text-white">
                  $ <countTo
                    :start-val="Number(item.price)"
                    :end-val="Number(item.preferentialPrice)"
                    :duration="3000"
                    :decimals="2"
                  />
                </div>
                <div class="subscribe-text-3 text-12px line-through">
                  $ {{ item.price }}
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="px-4px text-10px text-n-text-3 pb-24px">
        <p class="text-12px mt-6px">
          {{ config.texts.tips }}
        </p>
        <p class="mt-6px">
          {{ config.texts.tip1 }}
        </p>
        <p class="mt-6px">
          {{ config.texts.tip2 }}
        </p>
      </div>

      <!-- 支付方式选择器固定在底部 -->
      <div v-if="showPaymentMethodSelector" class="fixed bottom-0px left-0 right-0 flex justify-center">
        <PaymentMethodSelector
          variant="large"
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="selectPaymentMethod"
        />
      </div>
    </div>

    <BasicModal v-model="discountModel" :transparent="true" class-name="discount-modal">
      <div @click="buyNow">
        <div class="w-[200px] flex justify-center absolute top-[40px] left-[50%] ml-[-100px]">
          <div class="text-white text-30px font-bold italic">
            {{ discountNumber }}<span class="text-20px">%</span>
            <div class="relative top-[-5px]">
              OFF
            </div>
          </div>
        </div>
        <img :src="config.resources.discountBg" width="300px">
      </div>
    </BasicModal>
  </div>
</template>

<style scoped>
.member-wrap {
  min-height: calc(100vh - 192px);
}

.subscribe-cover {
  width: 100%;
  background: v-bind(vipBg);
  background-size: 100% auto;
}

.popular-tag {
  position: absolute;
  top: 0;
  left: 0;
  padding: 3px 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(109deg, #472D77 0%, #643867 100%);
  border-radius: 8px 0px 12px 0px;
  font-weight: 500;
  font-size: 10px;
  color: #CC42F7;
}
.cycleBg {
  background: v-bind(purpleTextGradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

.subscribe-title {
  background: linear-gradient(90deg, #9C5EF9 1%, #F86BCD 34%, #FDB547 65%, #FFE2A4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.subscribe-text-1 {
  font-size: 14px;
  color: #B59C7D;
}

.price-btn-subscribe {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  padding: 0 18px;
  border-radius: 14px;
  background: url('/images/vowpath/subscribe-btn.png') no-repeat center / 100% 100%;
}

.price-btn {
  display: inline-flex;
  align-items: center;
  padding: 7px 18px;
  border-radius: 14px;
  background: linear-gradient(45deg, rgba(100, 23, 255, 1) 0%, rgba(241, 80, 255, 1) 38%, rgba(241, 80, 255, 1) 70%, rgba(255, 236, 168, 1) 100%);
}

.discount-coins-text {
  font-weight: bold;
  font-size: 18px;
  background: linear-gradient(0deg, #9C5EF9 0%, #F86BCD 33%, #FDB547 65%, #FFE2A4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.subscribe-text-2 {
  color: v-bind(subscribeText2);
}
.subscribe-text-3 {
  color: v-bind(subscribeText3);
}
</style>

<style lang="scss">
.discount-modal {
  .close-icon {
    top: 12px;
    right: 0;
  }
}
</style>
